import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { AppLauncher } from '@capacitor/app-launcher';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { PushNotifications } from '@capacitor/push-notifications';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import type { PushPort } from '../push/PushSetup';
import type { BiometricPort } from '../biometric/BiometricLock';
import type { PermissionState } from '../permissions';
import type { MobilePermissions } from '@platform/shared-types';
import type { ShellPorts } from '../startup';

export interface CapacitorShell {
  ports: ShellPorts;
  platform: 'ios' | 'android';
  onResume(cb: () => void): () => void;
  onPause(cb: () => void): () => void;
  openUrl(url: string): Promise<void>;
  push: PushPort;
  biometric: BiometricPort;
}

/** Capacitor permission strings → the shell's four states. */
export function mapPermission(s: string | undefined): PermissionState {
  if (s === 'granted') return 'granted';
  if (s === 'denied') return 'denied';
  return 'prompt'; // 'prompt' | 'prompt-with-rationale' | unknown
}

/** Capgo's built-in bundle → null; otherwise OUR bundle id, which we pass to Capgo as `version`. */
export function mapCapgoCurrent(c: { bundle?: { id?: string; version?: string } }): string | null {
  const b = c?.bundle;
  if (!b || !b.version || b.id === 'builtin' || b.version === 'builtin') return null;
  return b.version;
}

const parseMaybeJson = (d: unknown): unknown => {
  if (typeof d !== 'string') return d;
  try { return JSON.parse(d); } catch { return d; }
};

/** Real ports over Capacitor plugins. `null` on the web: the gate then renders the app untouched. */
export function createCapacitorShell(opts: { permissions?: () => Promise<MobilePermissions> } = {}): CapacitorShell | null {
  if (!Capacitor.isNativePlatform()) return null;
  const platform = Capacitor.getPlatform() === 'android' ? 'android' : 'ios';
  let info: Promise<{ version: string; build: string }> | null = null;
  const appInfo = () => (info ??= App.getInfo());
  let dev: Promise<{ osVersion?: string; model?: string }> | null = null;
  const deviceInfo = () => (dev ??= Device.getInfo());

  const ports: ShellPorts = {
    store: {
      get: async (key) => (await Preferences.get({ key })).value ?? null,
      set: async (key, value) => { await Preferences.set({ key, value }); },
      remove: async (key) => { await Preferences.remove({ key }); },
    },
    http: {
      // Native HTTP: the WebView origin is capacitor://localhost, so a plain
      // fetch would be subject to CORS. Called directly — the global patch stays OFF.
      postJson: async (url, body, timeoutMs, headers) => {
        const r = await CapacitorHttp.post({
          url, data: body, headers: { 'Content-Type': 'application/json', ...(headers ?? {}) },
          connectTimeout: timeoutMs, readTimeout: timeoutMs,
        });
        return { status: r.status, data: parseMaybeJson(r.data) };
      },
    },
    device: {
      nativeVersion: async () => (await appInfo()).version,
      nativeBuild: async () => {
        const n = parseInt((await appInfo()).build, 10);
        if (!Number.isFinite(n)) throw new Error('native build number unreadable');
        return n;
      },
      osVersion: async () => (await deviceInfo()).osVersion,
      model: async () => (await deviceInfo()).model,
    },
    updater: {
      currentBundleId: async () => mapCapgoCurrent(await CapacitorUpdater.current()),
      notifyReady: async () => { await CapacitorUpdater.notifyAppReady(); },
      download: async ({ bundleId, url, sha256 }) => {
        const b = await CapacitorUpdater.download({ url, version: bundleId, checksum: sha256 });
        return { id: b.id };
      },
      applyNow: async (h) => { await CapacitorUpdater.set({ id: h.id }); },
      applyOnNextStart: async (h) => { await CapacitorUpdater.next({ id: h.id }); },
      resetToBuiltin: async () => { await CapacitorUpdater.reset({ toLastSuccessful: false }); },
    },
    permissions: opts.permissions ?? (async () => ({})),
    uuid: () => crypto.randomUUID(),
    now: () => Date.now(),
    sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
  };

  return {
    ports,
    platform,
    onResume(cb) {
      const handle = App.addListener('appStateChange', (s) => { if (s.isActive) cb(); });
      return () => { void handle.then((h) => h.remove()); };
    },
    onPause(cb) {
      const handle = App.addListener('appStateChange', (s) => { if (!s.isActive) cb(); });
      return () => { void handle.then((h) => h.remove()); };
    },
    openUrl: async (url) => { await AppLauncher.openUrl({ url }); },
    push: {
      checkPermission: async () => mapPermission((await PushNotifications.checkPermissions()).receive),
      requestPermission: async () => mapPermission((await PushNotifications.requestPermissions()).receive),
      register: () => new Promise<string>((resolve, reject) => {
        const subs: Promise<{ remove: () => Promise<void> }>[] = [];
        const done = () => { subs.forEach((p) => void p.then((h) => h.remove())); clearTimeout(timer); };
        const timer = setTimeout(() => { done(); reject(new Error('push registration timed out')); }, 15000);
        subs.push(PushNotifications.addListener('registration', (t) => { done(); resolve(t.value); }));
        subs.push(PushNotifications.addListener('registrationError', (e) => { done(); reject(new Error(String((e as any)?.error ?? e))); }));
        void PushNotifications.register().catch((e) => { done(); reject(e); });
      }),
      onTap(cb) {
        const handle = PushNotifications.addListener('pushNotificationActionPerformed', (a) => {
          const d = (a?.notification?.data as any)?.deepLink;
          cb(typeof d === 'string' ? d : null);
        });
        return () => { void handle.then((h) => h.remove()); };
      },
    },
    biometric: {
      isAvailable: async () => {
        try { return !!(await NativeBiometric.isAvailable()).isAvailable; } catch { return false; }
      },
      verify: async (reason) => {
        // useFallback: the device passcode works when Face ID / fingerprint won't.
        try { await NativeBiometric.verifyIdentity({ reason, title: 'RubyTill', useFallback: true }); return true; } catch { return false; }
      },
    },
  };
}
